import { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './lib/auth';
import { DataProvider, useData } from './lib/data';
import { SelectionProvider, useSelection } from './lib/selection';
import { ToastProvider } from './lib/toast';
import { KeyboardShortcuts } from './lib/shortcuts';
import { isAllowedEmail } from './lib/supabase';
import { Login } from './views/Login';
import { CommandK } from './components/CommandK';
import { QuickAdd } from './components/QuickAdd';
import { QuickAddProvider, useQuickAdd } from './lib/quickAdd';
import { ActivityPanel } from './components/ActivityPanel';
import { ErrorBoundary } from './components/ErrorBoundary';
import { VariantSidebar } from './variants/VariantSidebar';

function Shell() {
  const { user, loading, signOut } = useAuth();

  if (loading) {
    return (
      <div className="h-full grid place-items-center text-muted font-mono text-sm">
        loading…
      </div>
    );
  }

  if (!user) return <Login />;

  if (!isAllowedEmail(user.email)) {
    return (
      <div className="h-full grid place-items-center p-8">
        <div className="panel p-6 max-w-md text-center space-y-3">
          <div className="text-red font-mono text-sm">ACCESS DENIED</div>
          <div className="text-muted text-sm">
            <code className="text-ink">{user.email}</code> is not on the allowlist.
          </div>
          <button className="btn" onClick={signOut}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ToastProvider>
        <DataProvider>
          <SelectionProvider>
            <QuickAddProvider>
              <div className="h-full flex flex-col">
                <TopBar userEmail={user.email ?? ''} onSignOut={signOut} />
                <main className="flex-1 flex overflow-hidden">
                  <ErrorBoundary>
                    <div className="flex-1 overflow-hidden">
                      <VariantSidebar />
                    </div>
                  </ErrorBoundary>
                  <ActivityPanelController />
                </main>
              </div>
              <CommandK />
              <QuickAdd />
              <KeyboardShortcuts />
            </QuickAddProvider>
          </SelectionProvider>
        </DataProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

function ActivityPanelController() {
  const { activityOpen, toggleActivity } = useSelection();
  if (!activityOpen) return null;
  return <ActivityPanel onClose={toggleActivity} />;
}

function TopBar({
  userEmail,
  onSignOut,
}: {
  userEmail: string;
  onSignOut: () => void;
}) {
  // activityOpen is inside SelectionProvider, so we use a child component.
  return (
    <header className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-border bg-panel/80 backdrop-blur shrink-0">
      <div className="flex items-center gap-4 min-w-0">
        <a
          href="#/"
          className="font-mono text-xs tracking-widest text-amberHi shrink-0 hover:brightness-125"
        >
          ◆ TASK BOARD
        </a>
        <a
          href="https://forge-portal-three.vercel.app"
          target="_blank"
          rel="noopener noreferrer"
          title="Factory Control · dispatches, live terminal, machines"
          className="hidden sm:inline-flex items-center gap-1 font-mono text-[10px] tracking-widest
                     text-dim hover:text-amberHi transition-colors shrink-0"
        >
          factory control
          <span className="text-[9px] opacity-60">↗</span>
        </a>
      </div>
      <div className="flex items-center gap-2 text-xs font-mono text-muted shrink-0">
        <QuickAddButton />
        <SyncStatus />
        <ActivityToggleButton />
        <span className="hidden xl:inline">{userEmail}</span>
        <button className="btn text-xs" onClick={onSignOut}>
          sign out
        </button>
      </div>
    </header>
  );
}

function QuickAddButton() {
  const { show } = useQuickAdd();
  return (
    <button
      type="button"
      onClick={show}
      title="Add task (n)"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono font-semibold
                 bg-amber border border-amber text-bg
                 hover:brightness-110 transition"
    >
      <span className="text-base leading-none">+</span>
      <span className="hidden sm:inline">add task</span>
      <kbd className="hidden md:inline border border-bg/30 rounded px-1 text-[10px] ml-1">n</kbd>
    </button>
  );
}

function ActivityToggleButton() {
  const { activityOpen, toggleActivity } = useSelection();
  return (
    <button
      onClick={toggleActivity}
      className={
        'btn text-xs ' +
        (activityOpen ? 'border-amber/60 text-white bg-amber/15' : '')
      }
      title="Background activity panel"
    >
      <span className="status-dot status-ok" />
      activity
    </button>
  );
}

function SyncStatus() {
  const { realtimeStatus, lastEventAt, eventCount, recentEvents } = useData();
  const [pulse, setPulse] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (eventCount === 0) return;
    setPulse(true);
    const t = setTimeout(() => setPulse(false), 800);
    return () => clearTimeout(t);
  }, [eventCount]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest('[data-sync-popover]')) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const relative = useRelativeTime(lastEventAt);

  const label = realtimeStatus === 'connected'
    ? lastEventAt
      ? `synced · ${relative}`
      : 'synced'
    : realtimeStatus === 'connecting'
      ? 'sync…'
      : realtimeStatus === 'offline'
        ? 'offline — no network'
        : realtimeStatus === 'error'
          ? 'sync error'
          : 'closed';

  const dotCls = {
    connecting: 'status-degraded',
    connected: 'status-ok',
    error: 'status-down',
    closed: 'status-down',
    offline: 'status-down',
  }[realtimeStatus];

  const borderCls =
    realtimeStatus === 'connected'
      ? 'border-green/40 text-green'
      : realtimeStatus === 'offline' || realtimeStatus === 'error' || realtimeStatus === 'closed'
        ? 'border-red/40 text-red'
        : 'border-border text-muted';

  return (
    <div data-sync-popover className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={
          'inline-flex items-center gap-1.5 px-2 py-1 rounded font-mono text-[10px] border transition-all ' +
          borderCls +
          (pulse ? ' bg-green/15 border-green/80 scale-105' : '')
        }
        title="click to see realtime event log"
      >
        <span
          className={
            `status-dot ${dotCls} transition-all ` +
            (pulse ? 'ring-2 ring-green/60 scale-150' : '')
          }
        />
        <span className="hidden sm:inline">{label}</span>
        {eventCount > 0 && (
          <span className="hidden md:inline text-dim">· {eventCount}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-[360px] panel shadow-hud max-h-[70vh] flex flex-col">
          <div className="px-3 py-2 border-b border-border flex items-baseline justify-between">
            <div className="text-xs font-mono text-amberHi tracking-widest uppercase">
              sync log
            </div>
            <div className="text-[10px] font-mono text-dim">
              {eventCount} events · {realtimeStatus}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {recentEvents.length === 0 && (
              <div className="text-[11px] text-dim font-mono p-3 text-center">
                no events yet — try editing anything
              </div>
            )}
            {recentEvents.map((e) => (
              <div
                key={e.id}
                className="panel-hi p-2 rounded text-[10px] font-mono flex items-center gap-2"
              >
                <span
                  className={
                    'shrink-0 ' +
                    (e.eventType === 'INSERT'
                      ? 'text-green'
                      : e.eventType === 'DELETE'
                        ? 'text-red'
                        : 'text-amberHi')
                  }
                >
                  {e.eventType}
                </span>
                <span className="text-muted truncate flex-1">
                  {e.table} · {e.rowId || '—'}
                </span>
                <span className="text-dim shrink-0">{relativeShort(e.at)}</span>
              </div>
            ))}
          </div>
          <div className="px-3 py-2 border-t border-border text-[10px] font-mono text-dim">
            websocket: {realtimeStatus === 'connected' ? '✓ connected' : realtimeStatus}
          </div>
        </div>
      )}
    </div>
  );
}

function relativeShort(iso: string): string {
  const secs = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 2) return 'now';
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  return `${Math.round(secs / 3600)}h`;
}

function useRelativeTime(iso: string | null): string {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return 'idle';
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 2) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}

export function App() {
  return (
    <AuthProvider>
      <Shell />
    </AuthProvider>
  );
}
